import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface UseAIOnboardingModalPaginationOptions {
  isOpen: boolean;
}

export function useAIOnboardingModalPagination({
  isOpen,
}: UseAIOnboardingModalPaginationOptions) {
  const pageToCursor = useRef<Record<number, string>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [reviewedSuggestionPagesByID, setReviewedSuggestionPagesByID] =
    useState<Record<string, number>>({});

  const resetPagination = useCallback(() => {
    pageToCursor.current = {};
    setCurrentPage(0);
    setReviewedSuggestionPagesByID({});
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetPagination();
    }
  }, [isOpen, resetPagination]);

  const onNextPage = useCallback(
    (endCursor: unknown) => {
      if (typeof endCursor !== 'string') {
        return;
      }

      pageToCursor.current[currentPage] = endCursor;
      setCurrentPage((page) => page + 1);
    },
    [currentPage]
  );

  const onPreviousPage = useCallback(() => {
    setCurrentPage((page) => Math.max(page - 1, 0));
  }, []);

  const onSuggestionReviewed = useCallback(
    (suggestionID: string) => {
      setReviewedSuggestionPagesByID((reviewedPages) => {
        if (reviewedPages[suggestionID] !== undefined) {
          return reviewedPages;
        }

        return { ...reviewedPages, [suggestionID]: currentPage };
      });
    },
    [currentPage]
  );

  const reviewedSuggestionIDs = useMemo(
    () => Object.keys(reviewedSuggestionPagesByID),
    [reviewedSuggestionPagesByID]
  );
  const reviewedCountBeforeCurrentPage = useMemo(
    () =>
      Object.values(reviewedSuggestionPagesByID).filter(
        (page) => page < currentPage
      ).length,
    [currentPage, reviewedSuggestionPagesByID]
  );

  return {
    after:
      currentPage === 0
        ? null
        : (pageToCursor.current[currentPage - 1] ?? null),
    currentPage,
    hasPreviousPage: currentPage > 0,
    onNextPage,
    onPreviousPage,
    onSuggestionReviewed,
    reviewedCountBeforeCurrentPage,
    reviewedSuggestionIDs,
  };
}
